import React from "react";
import { Alert, Card, Skeleton, Space, Switch, Tag, Typography } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import * as api from "../../Api/index";

const { Title, Text, Paragraph } = Typography;

const errorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Something went wrong";

// The authenticator-app switch for every CBX app. Off by default: sign-in is
// email + password only until it is turned on here.
export default function SecuritySettings() {
  const [settings, setSettings] = React.useState(null);
  const [saving, setSaving] = React.useState(null);

  React.useEffect(() => {
    api
      .getTwoFactorSettings()
      .then(({ data }) => setSettings(data))
      .catch((error) => toast.error(errorMessage(error)));
  }, []);

  const save = async (field, value) => {
    setSaving(field);
    try {
      const { data } = await api.updateTwoFactorSettings({ [field]: value });
      setSettings(data);
      toast.success("Saved");
    } catch (error) {
      toast.error(errorMessage(error), { duration: 8000 });
    } finally {
      setSaving(null);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "90px 18px 40px",
        background:
          "linear-gradient(135deg, #eef4ff 0%, #f8fafc 45%, #eefdf5 100%)",
      }}
    >
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <Space align="center" style={{ marginBottom: 18 }}>
          <SafetyCertificateOutlined style={{ fontSize: 28, color: "#1677ff" }} />
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Sign-in Security
            </Title>
            <Text type="secondary">Applies to every CBX app.</Text>
          </div>
        </Space>

        <Card
          bordered={false}
          style={{
            borderRadius: 24,
            boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
          }}
          bodyStyle={{ padding: 24 }}
        >
          {!settings ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <>
              <SettingRow
                title="Authenticator app (two-step sign-in)"
                description="Users can set up Google Authenticator, Microsoft Authenticator or Authy from Settings → Security in the CBX portal. Anyone who has set it up is asked for its 6-digit code after their password. Turning this off goes back to password-only sign-in for everyone; their apps stay set up for when it is turned on again."
                checked={settings.twoFactorEnabled}
                loading={saving === "twoFactorEnabled"}
                onChange={(value) => save("twoFactorEnabled", value)}
              />

              <div
                style={{ borderTop: "1px solid #e2e8f0", margin: "18px 0" }}
              />

              <SettingRow
                title="Require it for everyone"
                description="Users without an authenticator app must set one up the next time they sign in, and can't turn it off. Set up your own app first, so you aren't locked out."
                checked={settings.twoFactorRequired}
                disabled={!settings.twoFactorEnabled}
                loading={saving === "twoFactorRequired"}
                onChange={(value) => save("twoFactorRequired", value)}
              />

              <Alert
                style={{ marginTop: 20, borderRadius: 14 }}
                type={settings.twoFactorEnabled ? "success" : "info"}
                showIcon
                message={
                  settings.twoFactorEnabled
                    ? `${settings.usersWithApp} of ${settings.totalUsers} users have set up an authenticator app.`
                    : "Off: users sign in with their email and password only."
                }
                description="A user who loses their phone can use one of their backup codes, or you can remove their app from Manage Users → Edit."
              />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

function SettingRow({ title, description, checked, disabled, loading, onChange }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 20,
        alignItems: "flex-start",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <div>
        <Text strong style={{ fontSize: 15 }}>
          {title}{" "}
          <Tag color={checked && !disabled ? "green" : "default"}>
            {checked && !disabled ? "On" : "Off"}
          </Tag>
        </Text>
        <Paragraph type="secondary" style={{ margin: "6px 0 0" }}>
          {description}
        </Paragraph>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        loading={loading}
        onChange={onChange}
      />
    </div>
  );
}
